import { keepEvidenceActivationLocal, EvidenceDisclosure, DebugDetails } from './chunk-CC7OMOQ3.js';
import { useLadderShared } from './chunk-MOQIOZ7K.js';
import { ChainAvatar } from './chunk-UHTGLWOP.js';
import { useSignedCdnImage } from './chunk-3JVPQKNG.js';
import { useQuickActions, opensInApp, QUICK_ACTION_CLASS, MiddleEllipsis, RichTooltip } from './chunk-FXNXAG5K.js';
import { TooltipHint, CopyButton } from './chunk-DIDPLGSY.js';
import { Badge, badgeVariants } from './chunk-2WDEGOCV.js';
import { Popover, PopoverTrigger, PopoverContent } from './chunk-DPDYXXQM.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React3 from 'react';
import { initials, resolveAttributionChipPrefs } from '@authe/fe-commons/display';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { RiskLevel, CatalogLifecycle, Severity, riskLevelFromWire } from '@authe/fe-commons/precedence';
import { Info, AlertTriangle, ArrowUpRight, ExternalLink, Tag, Boxes, Search, FileCode2, UserRound, Star, BadgeCheck } from 'lucide-react';
import { TRANSACTION_FLAG_META } from '@authe/fe-commons/spam';
import { impersonationSentence } from '@authe/fe-commons/tokens';
import { ConfidenceTier, readConfidencePolicy, DEFAULT_TIER_CUTOFFS, tierFor, customerData, coerceConfidence, meetsReadConfidence, customerText, clampConfidence, formatConfidence } from '@authe/fe-commons/evidence';
import { isDebugEnabled } from '@authe/fe-commons/config';
import { normalizeAddress, hasEvmIdentifiers, resolveChain, NATIVE_TOKEN, validatorIndexOf, VALIDATOR_TITLE, validatorLabel, syntheticPartyName, getExplorerUrl, chainDisplayName } from '@authe/fe-commons/chains';
import { focusAddress } from '@authe/fe-commons/focus';
import { addressOverlayForContext, abiSummaryForContext, proxySummaryForContext, addressAbiLabel } from '@authe/fe-commons/overlays';
import { severityLabel } from '@authe/fe-commons/addresses';
import { BUNDLE_KEY, scoredConfidence } from '@authe/fe-commons/attribution';

var SIZE = {
  sm: "size-5",
  md: "size-7"
};
function AddressAvatar({
  logoUrl,
  color,
  name,
  entityName,
  nativeTitle = true,
  size = "md",
  className
}) {
  const img = useSignedCdnImage(logoUrl);
  const [failedSrc, setFailedSrc] = React3.useState();
  const monogram = entityName ? initials(entityName) : "";
  if (!logoUrl && !color && !monogram) return null;
  const dim = SIZE[size];
  const title = nativeTitle ? name || entityName || void 0 : void 0;
  if (logoUrl && img.src && failedSrc !== img.src) {
    return /* @__PURE__ */ jsx(
      "img",
      {
        src: img.src,
        ...img.crossOrigin ? { crossOrigin: img.crossOrigin } : {},
        alt: "",
        title,
        onError: () => setFailedSrc(img.src),
        className: cn("shrink-0 rounded-md border border-border bg-background object-contain", dim, className)
      }
    );
  }
  if (!color && !monogram) return null;
  return /* @__PURE__ */ jsx(
    "span",
    {
      "data-testid": "address-avatar-fallback",
      className: cn(
        "inline-flex shrink-0 items-center justify-center rounded-md border text-3xs font-semibold leading-none",
        color ? "border-black/10 text-white" : "border-border bg-muted text-muted-foreground",
        dim,
        className
      ),
      style: color ? { backgroundColor: color } : void 0,
      title,
      "aria-hidden": true,
      children: monogram
    }
  );
}
var RISK_LEVEL_STYLES = {
  [RiskLevel.SAFE]: "bg-risk-none/15 text-risk-none border-risk-none/30",
  [RiskLevel.LOW]: "bg-risk-low/15 text-risk-low border-risk-low/30",
  [RiskLevel.MEDIUM]: "bg-risk-medium/15 text-risk-medium border-risk-medium/30",
  [RiskLevel.HIGH]: "bg-risk-high/15 text-risk-high border-risk-high/30",
  [RiskLevel.SEVERE]: "bg-risk-critical/15 text-risk-critical border-risk-critical/30"
};
var RISK_LEVEL_LABELS = {
  [RiskLevel.SAFE]: "Safe",
  [RiskLevel.LOW]: "Low",
  [RiskLevel.MEDIUM]: "Medium",
  [RiskLevel.HIGH]: "High",
  [RiskLevel.SEVERE]: "Severe"
};
function RiskBadge({ level, score, className }) {
  const rung = riskLevelFromWire(level);
  if (!rung) return null;
  return /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: cn("border", RISK_LEVEL_STYLES[rung], className), children: [
    /* @__PURE__ */ jsx("span", { children: RISK_LEVEL_LABELS[rung] }),
    typeof score === "number" && // Weight, not opacity. `opacity-70` over the chip fill measured
    // 2.9-3.4:1 in BOTH themes — below the bar even after the `--risk-*`
    // ramp was retuned, because it fades the text towards the very
    // background it has to stand out from. The number reads as secondary
    // because it is a numeral in a lighter weight beside a semibold word.
    /* @__PURE__ */ jsx("span", { className: "ml-1 font-normal tabular-nums", children: score.toFixed(0) })
  ] });
}
var TONES = {
  // A loss that already happened — solid, and the loudest thing in a row.
  critical: {
    box: "border-risk-critical/60 bg-risk-critical/15",
    icon: "text-risk-critical",
    title: "text-risk-critical",
    chip: "border-risk-critical bg-risk-critical text-status-foreground",
    Icon: AlertTriangle
  },
  alert: {
    box: "border-risk-high/40 bg-risk-high/10",
    icon: "text-risk-high",
    title: "text-risk-high",
    chip: "border-risk-high/40 bg-risk-high/10 text-risk-high",
    Icon: AlertTriangle
  },
  notice: {
    box: "border-muted-foreground/30 bg-muted",
    icon: "text-muted-foreground",
    title: "text-foreground",
    chip: "border-muted-foreground/30 bg-muted text-muted-foreground",
    Icon: Info
  }
};
function TransactionFlagNotice({
  flag,
  headline,
  className
}) {
  if (!flag) return null;
  const meta = TRANSACTION_FLAG_META[flag.kind];
  const tone = TONES[meta.tone];
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: meta.tone === "notice" ? "note" : "alert",
      "data-flag-kind": flag.kind,
      className: cn("flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5", tone.box, className),
      children: [
        /* @__PURE__ */ jsx(tone.Icon, { className: cn("mt-0.5 size-4 shrink-0", tone.icon), "aria-hidden": true }),
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-0.5", children: [
          /* @__PURE__ */ jsx("p", { className: cn("text-sm font-semibold", tone.title), children: headline ?? flag.label }),
          /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: flag.reason })
        ] })
      ]
    }
  );
}
function transactionFlagChipClass(kind) {
  return TONES[TRANSACTION_FLAG_META[kind].tone].chip;
}
function TransactionFlagBadge({
  flag,
  className
}) {
  const tone = TONES[TRANSACTION_FLAG_META[flag.kind].tone];
  return /* @__PURE__ */ jsx(
    Badge,
    {
      variant: "outline",
      title: flag.reason,
      "data-flag-kind": flag.kind,
      className: cn("font-normal", tone.chip, className),
      children: flag.label
    }
  );
}
function CounterfeitMark({
  claim,
  className
}) {
  const sentence = impersonationSentence(claim);
  return /* @__PURE__ */ jsxs(
    "span",
    {
      "data-counterfeit": true,
      role: "note",
      "aria-label": sentence,
      title: sentence,
      className: cn(
        // The counterfeit flag's own tone, so the mark and the page's
        // "Counterfeit token" notice are one colour (it was `destructive`).
        "inline-flex shrink-0 items-center gap-0.5 rounded-sm border px-1 text-3xs font-medium uppercase leading-4 tracking-wide",
        transactionFlagChipClass("counterfeit_token"),
        className
      ),
      children: [
        /* @__PURE__ */ jsx(AlertTriangle, { className: "size-2.5", "aria-hidden": true }),
        "Fake"
      ]
    }
  );
}
var STYLES = {
  [CatalogLifecycle.VERIFIED]: "bg-success/15 text-success border-success/30",
  [CatalogLifecycle.CANDIDATE]: "text-muted-foreground border-border",
  [CatalogLifecycle.DEPRECATED]: "text-warning border-warning/40",
  [CatalogLifecycle.QUARANTINED]: "bg-destructive/10 text-destructive border-destructive/30"
};
var LABELS = {
  [CatalogLifecycle.VERIFIED]: "Verified",
  [CatalogLifecycle.CANDIDATE]: "Candidate",
  [CatalogLifecycle.DEPRECATED]: "Deprecated",
  [CatalogLifecycle.QUARANTINED]: "Quarantined"
};
var DESCRIPTIONS = {
  [CatalogLifecycle.VERIFIED]: "Verified: a person or a trusted process confirmed this record.",
  [CatalogLifecycle.CANDIDATE]: "Candidate: this record was discovered automatically and nobody has curated it yet. It is used, but not confirmed.",
  [CatalogLifecycle.DEPRECATED]: "Deprecated: superseded, and de-prioritised in queries.",
  [CatalogLifecycle.QUARANTINED]: "Quarantined: suppressed as untrusted, pending review."
};
function lifecycleDescription(lifecycle) {
  return DESCRIPTIONS[lifecycle ?? CatalogLifecycle.CANDIDATE];
}
function LifecycleBadge({ lifecycle, className }) {
  const key = lifecycle ?? CatalogLifecycle.CANDIDATE;
  const style = STYLES[key] ?? "text-muted-foreground border-border";
  const label = LABELS[key] ?? String(lifecycle ?? "candidate");
  return /* @__PURE__ */ jsx(
    Badge,
    {
      variant: "outline",
      className: cn("border text-2xs capitalize", style, className),
      title: DESCRIPTIONS[key],
      children: label
    }
  );
}
var CutoffsContext = React3.createContext(null);
function ConfidenceCutoffsProvider({
  cutoffs,
  children
}) {
  const value = React3.useMemo(
    () => cutoffs && (cutoffs.high != null || cutoffs.medium != null) ? { high: cutoffs.high, medium: cutoffs.medium } : null,
    [cutoffs?.high, cutoffs?.medium]
  );
  return /* @__PURE__ */ jsx(CutoffsContext.Provider, { value, children });
}
function useTierCutoffs() {
  const scoped = React3.useContext(CutoffsContext);
  return {
    high: scoped?.high ?? DEFAULT_TIER_CUTOFFS.high,
    medium: scoped?.medium ?? DEFAULT_TIER_CUTOFFS.medium
  };
}
function useTierFor() {
  const cutoffs = useTierCutoffs();
  return React3.useCallback(
    (score) => tierFor(score, cutoffs),
    [cutoffs.high, cutoffs.medium]
  );
}
function useCutoffsAreScoped() {
  const scoped = React3.useContext(CutoffsContext);
  return scoped != null;
}
var KIND_PHRASE = {
  trace_opcode: "stated by the trace opcode",
  call_shape: "classified from the call's shape",
  registry: "pinned by the protocol registry",
  abi: "decoded using an ABI or interface",
  proxy_abi: "decoded from the proxy implementation ABI",
  wire_layout: "decoded from a fixed wire layout",
  provided: "supplied by an upstream producer",
  generic: "built by the generic fallback",
  heuristic: "inferred heuristically",
  provider: "asserted by provider",
  user: "asserted by a human",
  automation: "asserted by a trusted process",
  model: "predicted by model",
  /**
   * The EXTERNAL-audience phrase, and the one most likely to be rendered to a
   * customer: `EvidenceSource.redacted()` collapses every INTERNAL (third-party)
   * source to `SourceKind.AGGREGATE` precisely so the provider we scraped is
   * never named. It was missing here, and the lookup below falls back to the raw
   * kind — so the redaction path, the whole reason the visibility split exists,
   * rendered the bare token "aggregate" to exactly the audience it protects.
   */
  aggregate: "aggregated from multiple sources",
  unknown: "of unknown origin"
};
var TIER_BAR = {
  [ConfidenceTier.HIGH]: "bg-success",
  [ConfidenceTier.MEDIUM]: "bg-warning",
  [ConfidenceTier.LOW]: "bg-destructive"
};
function explainEvidence(ev) {
  ev = customerData(ev);
  const source = ev.source ?? {};
  const phrase = source.kind ? KIND_PHRASE[source.kind] ?? source.kind : "of unknown origin";
  const signals = ev.signals ?? {};
  const isModel = source.kind === "model";
  const who = isModel ? modelWho(phrase, source.id, signals) : source.id ? `${phrase} ${source.id}` : phrase;
  const score = coerceConfidence(ev.confidence);
  const bullets = [
    score == null ? who : `${who} (confidence ${score.toFixed(2)})`
  ];
  if (isModel && signals.human_approved) bullets.push("approved by a human reviewer");
  for (const reason of ev.reasons ?? []) bullets.push(reason);
  const active = Object.entries(signals).filter(([k, v]) => Boolean(v) && !(isModel && MODEL_IDENTITY_SIGNALS.has(k))).map(([k]) => k).sort();
  if (active.length > 0) bullets.push(`signals: ${active.join(", ")}`);
  if (source.url) bullets.push(`source: ${source.url}`);
  return bullets;
}
var MODEL_IDENTITY_SIGNALS = /* @__PURE__ */ new Set([
  "model_name",
  "model_version",
  "variant",
  "features_hash",
  "is_shadow",
  "human_approved"
]);
function modelWho(phrase, id, signals) {
  const name = signals.model_name;
  if (!name) return id ? `${phrase} ${id}` : phrase;
  const label = [name, signals.model_version].filter(Boolean).map(String).join(" ");
  const arm = signals.variant ? [String(signals.variant)] : [];
  if (signals.is_shadow && !arm.includes("shadow")) arm.push("shadow");
  return `${phrase} ${label}` + (arm.length ? ` (${arm.join(", ")})` : "");
}
function byConfidence(evidence) {
  return [...evidence].sort(
    (a, b) => (coerceConfidence(b.confidence) ?? 0) - (coerceConfidence(a.confidence) ?? 0)
  );
}
function panelContent({ explanation, evidence, confidence, confidenceNote, record, lifecycle, details, diagnostics }) {
  const clean = (lines) => [...new Set((Array.isArray(lines) ? lines : []).filter((line) => typeof line === "string").map((line) => customerText(line).replace(/^(?:\s*[-•·]\s*)+/, "").trim().replace(/^decoded from a verified ABI(?= |$)/, KIND_PHRASE.abi)).filter(Boolean))];
  const persisted = clean(explanation);
  const evidenceList = (Array.isArray(evidence) ? evidence : evidence ? [evidence] : []).filter((item) => !!item && typeof item === "object");
  const allBullets = persisted.length ? persisted : clean(byConfidence(evidenceList).flatMap(explainEvidence));
  const bullets = allBullets.filter((line) => !/^signals:\s/i.test(line));
  const diagnosticLines = isDebugEnabled() ? clean([...allBullets.filter((line) => /^signals:\s/i.test(line)), ...clean(diagnostics)]) : [];
  const score = coerceConfidence(confidence);
  const note = score == null && typeof confidenceNote === "string" ? customerText(confidenceNote).trim() : "";
  const recorded = clean(record).filter((line) => !bullets.includes(line));
  if (!bullets.length && score == null && !note && !lifecycle && !recorded.length && !details && !diagnosticLines.length) return null;
  return { bullets, score, note, recorded, diagnosticLines };
}
function WhyList({ lines }) {
  if (!lines.length) return null;
  return /* @__PURE__ */ jsx("ul", { className: "ml-4 list-disc space-y-1 text-muted-foreground", children: lines.map((line) => /* @__PURE__ */ jsx("li", { className: "leading-relaxed", children: line }, line)) });
}
function WhyPanelContent({ content, ...props }) {
  const scopedCutoffs = useTierCutoffs();
  const { cutoffs, lifecycle, title = "Why?", className, maxVisibleReasons = 3, details, detailsLabel, showConfidence = true } = props;
  const { bullets, score, note, recorded, diagnosticLines } = content;
  const limit = Number.isFinite(maxVisibleReasons) ? Math.max(1, Math.floor(maxVisibleReasons)) : 3;
  const sources = bullets.filter((line) => /^source:\s/i.test(line));
  const reasons = bullets.filter((line) => !/^source:\s/i.test(line));
  const recordOnly = !bullets.length && !details;
  const primary = recordOnly ? recorded : reasons;
  const more = primary.slice(limit);
  const history = recordOnly ? [] : recorded;
  const hasDetails = more.length > 0 || sources.length > 0 || history.length > 0 || !!details;
  const pct = score == null ? null : clampConfidence(score);
  return /* @__PURE__ */ jsxs("div", { className: cn("min-w-0 space-y-3 text-xs [overflow-wrap:anywhere]", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: title }),
      lifecycle && /* @__PURE__ */ jsx(LifecycleBadge, { lifecycle })
    ] }),
    showConfidence && pct != null && /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between text-2xs text-muted-foreground", children: [
        /* @__PURE__ */ jsx("span", { children: "Confidence" }),
        /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: formatConfidence(pct) })
      ] }),
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "h-1.5 w-full overflow-hidden rounded-full bg-muted",
          role: "progressbar",
          "aria-label": "Evidence confidence",
          "aria-valuenow": Math.round(pct * 100),
          "aria-valuemin": 0,
          "aria-valuemax": 100,
          children: /* @__PURE__ */ jsx(
            "div",
            {
              className: cn("h-full rounded-full", TIER_BAR[tierFor(pct, { ...scopedCutoffs, ...cutoffs })]),
              style: { width: `${Math.round(pct * 100)}%` }
            }
          )
        }
      )
    ] }),
    note && /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: note }),
    recordOnly && recorded.length > 0 && /* @__PURE__ */ jsx("p", { className: "font-medium", children: "Record history" }),
    /* @__PURE__ */ jsx(WhyList, { lines: primary.slice(0, limit) }),
    !bullets.length && !recorded.length && !details && !diagnosticLines.length && /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: "No supporting evidence or explanation was recorded." }),
    hasDetails && /* @__PURE__ */ jsxs(
      EvidenceDisclosure,
      {
        className: "group/why border-t pt-2",
        summaryClassName: "py-1 text-foreground",
        contentClassName: "space-y-3 pt-2",
        summary: /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("span", { className: "group-open/why:hidden", children: detailsLabel ?? (more.length ? `More evidence (${more.length + sources.length + history.length})` : "Evidence details") }),
          /* @__PURE__ */ jsx("span", { className: "hidden group-open/why:inline", children: "Hide details" })
        ] }),
        children: [
          /* @__PURE__ */ jsx(WhyList, { lines: more }),
          sources.length > 0 && /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
            /* @__PURE__ */ jsx("p", { className: "font-medium", children: "Sources" }),
            /* @__PURE__ */ jsx(WhyList, { lines: sources })
          ] }),
          history.length > 0 && /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
            /* @__PURE__ */ jsx("p", { className: "font-medium", children: "Record history" }),
            /* @__PURE__ */ jsx(WhyList, { lines: history })
          ] }),
          details
        ]
      }
    ),
    diagnosticLines.length > 0 && /* @__PURE__ */ jsx(DebugDetails, { children: /* @__PURE__ */ jsx(WhyList, { lines: diagnosticLines }) })
  ] });
}
function WhyPanel(props) {
  const content = panelContent(props);
  return content ? /* @__PURE__ */ jsx(WhyPanelContent, { ...props, content }) : null;
}
function WhyPopover({ children, ...panel }) {
  const content = panelContent(panel);
  if (!content) return children == null ? null : /* @__PURE__ */ jsx(Fragment, { children });
  const label = panel.title && panel.title !== "Why?" ? `Why: ${panel.title}` : "Why this assessment?";
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex max-w-full items-center gap-1 align-middle", children: [
    children,
    /* @__PURE__ */ jsxs(Popover, { children: [
      /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          "aria-label": label,
          onKeyDown: keepEvidenceActivationLocal,
          onKeyUp: keepEvidenceActivationLocal,
          onClick: (event) => {
            event.stopPropagation();
          },
          className: cn(badgeVariants({ variant: "outline" }), "min-h-6 shrink-0 cursor-pointer border-dashed text-3xs text-muted-foreground hover:text-foreground focus-ring"),
          children: "Why?"
        }
      ) }),
      /* @__PURE__ */ jsx(
        PopoverContent,
        {
          align: "start",
          "aria-label": label,
          className: "w-80 ",
          onClick: (event) => event.stopPropagation(),
          children: /* @__PURE__ */ jsx(WhyPanelContent, { ...panel, content })
        }
      )
    ] })
  ] });
}
function AddressProxyDetails({ address, chain, summary, getContractHref, onNavigate, navigationEnabled = true }) {
  if (!summary?.is_proxy) return null;
  const historical = summary.basis === "block_state";
  const context = historical ? summary.context ?? void 0 : void 0;
  const recordedBlock = context?.block_number ?? summary.block_number;
  const follow = (event) => {
    event.stopPropagation();
    if (event.currentTarget.dataset.internal === "true" && onNavigate && opensInApp(event)) {
      event.preventDefault();
      onNavigate(event.currentTarget.getAttribute("href"));
    }
  };
  const linkProps = (target) => {
    const internal = getContractHref?.(target, chain, context);
    return { href: internal ?? getExplorerUrl(target, chain, "address") ?? void 0, "data-internal": Boolean(internal), onClick: follow };
  };
  const incomplete = summary.status !== "resolved" || !summary.implementations.length;
  const targetLink = (target) => !navigationEnabled ? /* @__PURE__ */ jsx("span", { className: "break-all font-mono", children: target }, target) : /* @__PURE__ */ jsx(
    "a",
    {
      ...linkProps(target),
      "aria-label": `Inspect proxy implementation ${target}`,
      className: "inline-flex min-w-0 max-w-full items-center hover:underline",
      children: /* @__PURE__ */ jsx(MiddleEllipsis, { value: target, kind: "address", monospace: true, title: target, className: "min-w-0" })
    },
    target
  );
  return /* @__PURE__ */ jsxs("section", { "data-address-proxy": true, "aria-label": "Contract implementation details", className: "space-y-3 text-sm", children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("p", { className: "font-medium", children: "Implementation contracts" }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "This address delegates code execution to another contract (a proxy)." }),
      /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
        chainDisplayName(chain),
        " \xB7 ",
        historical ? recordedBlock != null ? `Block ${recordedBlock.toLocaleString()}` : "Historical block observation" : "Latest stored observation"
      ] })
    ] }),
    summary.pattern && /* @__PURE__ */ jsxs("p", { children: [
      "Pattern: ",
      summary.pattern
    ] }),
    incomplete && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "The stored targets are not fully confirmed for this context. Discovery may be needed." }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", children: summary.implementations.map(targetLink) }),
    !summary.implementations.length && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No implementation is recorded yet. Inspect routing and discovery to resolve it." }),
    summary.implementations.length > 1 && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "These are recorded targets, not an ordered call path. Routing can depend on the function or input." }),
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: historical ? "Block-state metadata does not prove which target this call used. Targets can change within a block." : "This is the last stored resolution, not a live chain lookup." }),
    /* @__PURE__ */ jsxs("dl", { className: "grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs", children: [
      summary.valid_from_block != null && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("dt", { children: "Recorded from block" }),
        /* @__PURE__ */ jsx("dd", { children: summary.valid_from_block.toLocaleString() })
      ] }),
      summary.valid_until_block != null && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("dt", { children: "Recorded through block" }),
        /* @__PURE__ */ jsx("dd", { children: summary.valid_until_block.toLocaleString() })
      ] }),
      summary.confirmed_through_block != null && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("dt", { children: "Last confirmed block" }),
        /* @__PURE__ */ jsx("dd", { children: summary.confirmed_through_block.toLocaleString() })
      ] }),
      /* @__PURE__ */ jsx("dt", { children: "Human approved" }),
      /* @__PURE__ */ jsx("dd", { children: summary.human_approved ? "Yes" : "No" })
    ] }),
    navigationEnabled && /* @__PURE__ */ jsx("a", { ...linkProps(address), className: "inline-block text-primary hover:underline", children: "Inspect routing and upgrade history" }),
    /* @__PURE__ */ jsxs("details", { className: "border-t pt-2", children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer font-medium", children: "Why this proxy resolution?" }),
      /* @__PURE__ */ jsx(WhyPanel, { title: "Proxy confidence", className: "mt-2", confidence: summary.confidence, evidence: summary.evidence, explanation: summary.explanation, lifecycle: summary.lifecycle })
    ] })
  ] });
}
function ContractVerifiedMark() {
  return /* @__PURE__ */ jsx(
    "span",
    {
      "data-contract-verified": true,
      role: "img",
      "aria-label": "Contract source verified",
      title: "Contract source code verified for this address on this chain",
      className: "inline-flex shrink-0 text-primary",
      children: /* @__PURE__ */ jsx(BadgeCheck, { className: "size-3.5", "aria-hidden": "true" })
    }
  );
}
function AddressFactsBadge({ address, chain, overlay, proxy, abi, interactive = true, getContractHref, onNavigate, navigationEnabled = true }) {
  const facts = overlay?.addressFacts;
  const verified = overlay?.contractVerified === true;
  if (!facts && !verified && !abi && !proxy?.is_proxy) return null;
  const type = facts?.isContract === true ? "Contract" : facts?.isContract === false ? "Account" : proxy?.is_proxy ? "Contract" : "Address info";
  const Icon = type === "Contract" ? FileCode2 : type === "Account" ? UserRound : Info;
  const description = `View address type, source verification, contract interface and implementation details on ${chainDisplayName(chain)}`;
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Icon, { className: "size-3 shrink-0", "aria-hidden": true }),
    type,
    verified && /* @__PURE__ */ jsx(ContractVerifiedMark, {})
  ] });
  if (!interactive) return /* @__PURE__ */ jsx("span", { className: "inline-flex max-w-full shrink-0 flex-wrap items-center gap-1 whitespace-normal text-xs", title: type ?? "Address type unknown", children: content });
  return /* @__PURE__ */ jsxs(Popover, { children: [
    /* @__PURE__ */ jsx(TooltipHint, { label: description, children: /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-address-facts": true,
        "aria-label": `Address details for ${address} on ${chainDisplayName(chain)}`,
        onClick: (e) => e.stopPropagation(),
        className: "inline-flex max-w-full shrink-0 flex-wrap items-center gap-1 whitespace-normal rounded border px-1.5 py-0.5 text-left text-xs text-muted-foreground hover:bg-accent focus-ring",
        children: content
      }
    ) }) }),
    /* @__PURE__ */ jsxs(PopoverContent, { align: "start", className: "w-80 overflow-y-auto space-y-3 text-sm", onClick: (e) => e.stopPropagation(), children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsxs("p", { className: "font-medium", children: [
          "Address details \xB7 ",
          chainDisplayName(chain)
        ] }),
        /* @__PURE__ */ jsx("p", { className: "break-all font-mono text-xs text-muted-foreground", children: address })
      ] }),
      /* @__PURE__ */ jsxs("dl", { className: "grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-xs", children: [
        /* @__PURE__ */ jsx("dt", { children: "Address type" }),
        /* @__PURE__ */ jsx("dd", { children: type === "Account" ? "Account \xB7 no contract code recorded" : type === "Contract" ? "Contract" : "Unknown" }),
        /* @__PURE__ */ jsx("dt", { children: "Source code" }),
        /* @__PURE__ */ jsx("dd", { children: verified ? "Verified" : facts?.contractVerified === false ? "Unverified" : "Not recorded" }),
        /* @__PURE__ */ jsx("dt", { children: "Contract interface" }),
        /* @__PURE__ */ jsx("dd", { children: abi ? addressAbiLabel(abi) : "Not recorded for this context" }),
        /* @__PURE__ */ jsx("dt", { children: "Risk" }),
        /* @__PURE__ */ jsx("dd", { children: overlay?.riskLevel ? /* @__PURE__ */ jsx(RiskBadge, { level: overlay.riskLevel }) : "Not assessed" }),
        overlay?.riskScoreBasis === "measured" && typeof overlay.riskScore === "number" && Number.isFinite(overlay.riskScore) && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("dt", { children: "Risk score" }),
          /* @__PURE__ */ jsxs("dd", { children: [
            overlay.riskScore,
            " / 100"
          ] })
        ] }),
        /* @__PURE__ */ jsx("dt", { children: "Implementation" }),
        /* @__PURE__ */ jsx("dd", { children: proxy?.status === "resolved" && proxy.is_proxy === false ? "No delegation recorded" : proxy?.is_proxy === true ? proxy.status === "resolved" && proxy.implementations.length > 0 ? "Uses another contract" : "Not fully resolved" : "Not resolved for this context" })
      ] }),
      abi && /* @__PURE__ */ jsxs("details", { "data-address-abi": true, className: "space-y-2 text-xs", children: [
        /* @__PURE__ */ jsx("summary", { className: "cursor-pointer font-medium", children: "Contract interface details (ABI)" }),
        /* @__PURE__ */ jsxs("p", { className: "text-muted-foreground", children: [
          abi.basis === "block_state" ? `Recorded for block ${abi.context?.block_number?.toLocaleString()}.` : "Latest stored interface records.",
          " An interface describes how to read calls and events. Availability does not verify source code or guarantee complete decoding."
        ] }),
        /* @__PURE__ */ jsx("ul", { className: "max-h-48 space-y-2 overflow-y-auto", children: abi.records.map((record) => /* @__PURE__ */ jsxs("li", { children: [
          /* @__PURE__ */ jsxs("p", { className: "font-medium", children: [
            record.via === "implementation" ? "Proxy implementation" : "Contract",
            " \xB7 ",
            record.kind === "forensic" ? "Reconstructed from code or execution" : "Recorded interface"
          ] }),
          /* @__PURE__ */ jsx("p", { className: "break-all font-mono text-muted-foreground", children: record.address }),
          /* @__PURE__ */ jsxs("p", { children: [
            { system: "Shared catalog", customer: "Customer interface", org: "Organization interface", tenant: "Tenant interface", user: "Your interface" }[record.scope],
            record.origin ? ` \xB7 ${record.origin.replaceAll("_", " ")}` : "",
            record.reviewed ? " \xB7 Reviewed" : ""
          ] })
        ] }, `${record.via}:${record.address}`)) }),
        abi.records.some((r) => r.scope !== "system") && /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: "Your most specific applicable interface is shown: user, tenant, organization, customer, then shared. Actual decoding also evaluates the available evidence." }),
        abi.records.some((r) => r.kind === "forensic") && /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: "Forensic interfaces are reconstructed from execution or bytecode and may cover only part of the contract. Review does not make them source verified." }),
        abi.truncated && /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: "Only the first 16 recorded implementations were checked." })
      ] }),
      proxy?.is_proxy && /* @__PURE__ */ jsxs("details", { className: "space-y-2 border-t pt-2", children: [
        /* @__PURE__ */ jsxs("summary", { className: "cursor-pointer text-xs font-medium", children: [
          "Implementation details",
          proxy.implementations.length > 1 ? ` \xB7 ${proxy.implementations.length} contracts` : ""
        ] }),
        /* @__PURE__ */ jsx(AddressProxyDetails, { address, chain, summary: proxy, getContractHref, onNavigate, navigationEnabled })
      ] }),
      facts?.isContract === false && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "This does not establish wallet ownership or account control. Account types differ between chains." }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Type, source verification and risk are the latest stored records for this chain. They do not prove the state at the transaction's block." }),
      overlay?.riskScoreBasis === "category" && overlay.riskLevel && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Risk comes from recorded classifications and catalog findings. No independent numeric score is recorded." }),
      verified && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Source verification does not establish safety or verify a proxy's implementation." })
    ] })
  ] });
}
var AddressDisplayContext = React3.createContext({});
function addressOverlayName(overlay) {
  return overlay?.label ?? overlay?.primaryEntityName ?? void 0;
}
function useAddressDisplayName() {
  const { resolveOverlay } = React3.useContext(AddressDisplayContext);
  return React3.useCallback(
    (address, chain) => addressOverlayName(resolveOverlay?.(address, chain)) ?? null,
    [resolveOverlay]
  );
}
var noFormatter = () => void 0;
var noSubscription = () => () => void 0;
function useAddressDisplayValue(address, chain) {
  const { checksumFormatter } = React3.useContext(AddressDisplayContext);
  const canonical = normalizeAddress(address, chain);
  const eligible = /^0x[\da-f]{40}$/i.test(canonical) && (!chain || hasEvmIdentifiers(resolveChain(chain)?.family) || /^eip155:\d+$/.test(chain));
  const source = eligible ? checksumFormatter : void 0;
  const formatter = React3.useSyncExternalStore(source?.subscribe ?? noSubscription, source?.getSnapshot ?? noFormatter, noFormatter);
  return React3.useMemo(() => {
    if (!formatter || !eligible) return canonical;
    try {
      const formatted = formatter(canonical);
      return formatted.toLowerCase() === canonical.toLowerCase() ? formatted : canonical;
    } catch {
      return canonical;
    }
  }, [canonical, eligible, formatter]);
}
function AddressDisplayProvider({ children, ...defaults }) {
  const parent = React3.useContext(AddressDisplayContext);
  const value = React3.useMemo(() => ({
    navigationEnabled: defaults.navigationEnabled ?? parent.navigationEnabled,
    onNavigateContract: defaults.onNavigateContract ?? parent.onNavigateContract,
    proxyContext: defaults.proxyContext ?? parent.proxyContext,
    getContractHref: defaults.getContractHref ?? parent.getContractHref,
    checksumFormatter: defaults.checksumFormatter === void 0 ? parent.checksumFormatter : defaults.checksumFormatter,
    resolveOverlay: defaults.resolveOverlay ?? parent.resolveOverlay,
    resolveFallbackLabel: defaults.resolveFallbackLabel ?? parent.resolveFallbackLabel,
    getInternalHref: defaults.getInternalHref ?? parent.getInternalHref,
    onDrilldown: defaults.onDrilldown ?? parent.onDrilldown,
    onEditCatalog: defaults.onEditCatalog === void 0 ? parent.onEditCatalog : defaults.onEditCatalog,
    presentation: defaults.presentation ?? parent.presentation,
    size: defaults.size ?? parent.size,
    showAvatar: defaults.showAvatar ?? parent.showAvatar,
    showRiskBadge: defaults.showRiskBadge ?? parent.showRiskBadge,
    showExplorerIcon: defaults.showExplorerIcon ?? parent.showExplorerIcon,
    showDetails: defaults.showDetails ?? parent.showDetails,
    // A nested resolver owns its own batch (e.g. the contract explorer). It
    // must not also enqueue the same values in the outer fallback batch.
    registerAddress: defaults.registerAddress ?? (defaults.resolveOverlay ? void 0 : parent.registerAddress)
  }), [parent, defaults.navigationEnabled, defaults.onNavigateContract, defaults.proxyContext, defaults.getContractHref, defaults.checksumFormatter, defaults.onEditCatalog, defaults.resolveOverlay, defaults.resolveFallbackLabel, defaults.getInternalHref, defaults.onDrilldown, defaults.presentation, defaults.size, defaults.showAvatar, defaults.showRiskBadge, defaults.showExplorerIcon, defaults.showDetails, defaults.registerAddress]);
  return /* @__PURE__ */ jsx(AddressDisplayContext.Provider, { value, children });
}
function overlayTooltipRows(overlay) {
  const rows = [];
  if (overlay?.impersonates) rows.push({ label: "Counterfeit", value: impersonationSentence(overlay.impersonates) });
  const tags = overlay?.tags?.filter(Boolean) ?? [];
  if (tags.length > 0) rows.push({ label: tags.length === 1 ? "Tag" : "Tags", value: tags.join(", ") });
  return rows;
}
function AddressDisplay(props) {
  if (props.address === NATIVE_TOKEN) {
    const symbol = (props.chain ? resolveChain(props.chain)?.nativeToken : void 0) ?? props.label ?? "Native asset";
    return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex items-center gap-1", props.className), title: "Native asset \xB7 no contract address", children: [
      props.showChain && props.chain && /* @__PURE__ */ jsx(ChainAvatar, { chain: props.chain, size: "sm", showName: "sr-only" }),
      symbol
    ] });
  }
  const validator = validatorIndexOf(props.address);
  if (validator !== null) {
    return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex items-center gap-1", props.className), title: VALIDATOR_TITLE, children: [
      props.showChain && props.chain && /* @__PURE__ */ jsx(ChainAvatar, { chain: props.chain, size: "sm", showName: "sr-only" }),
      props.label ?? validatorLabel(validator)
    ] });
  }
  const party = syntheticPartyName(props.address);
  if (party !== null) {
    return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex items-center gap-1", props.className), title: party.title, children: [
      props.showChain && props.chain && /* @__PURE__ */ jsx(ChainAvatar, { chain: props.chain, size: "sm", showName: "sr-only" }),
      props.label ?? party.label
    ] });
  }
  return /* @__PURE__ */ jsx(AccountAddressDisplay, { ...props });
}
function AccountAddressDisplay({
  address,
  label: labelProp,
  fallbackLabel,
  entityLabel: entityLabelProp,
  chain,
  showChain = false,
  copyable = true,
  interactive = true,
  href,
  showExplorerIcon: showExplorerIconProp,
  className,
  monospace = true,
  internalHref: internalHrefProp,
  showInternalIcon = false,
  internalNewTab = false,
  onDrilldown: onDrilldownProp,
  onEditCatalog: onEditCatalogProp,
  tooltipRows: tooltipRowsProp,
  tooltipHeaderRight: tooltipHeaderRightProp,
  richTooltip,
  overlay: overlayProp,
  presentation: presentationProp,
  size: sizeProp,
  showAvatar: showAvatarProp,
  showRiskBadge: showRiskBadgeProp,
  showDetails: showDetailsProp,
  actions: actionsProp
}) {
  const defaults = React3.useContext(AddressDisplayContext);
  const actions = useQuickActions(actionsProp);
  React3.useEffect(() => {
    if (overlayProp === void 0 && address) return defaults.registerAddress?.(address, chain);
  }, [defaults.registerAddress, address, chain, overlayProp]);
  const overlay = addressOverlayForContext(overlayProp === void 0 ? defaults.resolveOverlay?.(address, chain) : overlayProp, address, chain);
  const abi = abiSummaryForContext(overlay?.abi, defaults.proxyContext);
  const proxy = proxySummaryForContext(overlay?.proxy, defaults.proxyContext);
  const navigationEnabled = defaults.navigationEnabled !== false;
  const internalHref = navigationEnabled ? internalHrefProp ?? (href ? void 0 : defaults.getInternalHref?.(address, chain)) : void 0;
  const onDrilldown = navigationEnabled ? onDrilldownProp ?? (href ? void 0 : defaults.onDrilldown) : void 0;
  const onEditCatalog = onEditCatalogProp === void 0 ? defaults.onEditCatalog : onEditCatalogProp;
  const presentation = presentationProp ?? defaults.presentation ?? "plain";
  const size = sizeProp ?? defaults.size ?? "md";
  const showAvatar = showAvatarProp ?? defaults.showAvatar ?? presentation === "chip";
  const showRiskBadge = showRiskBadgeProp ?? defaults.showRiskBadge ?? presentation === "chip";
  const showExplorerIcon = showExplorerIconProp ?? defaults.showExplorerIcon ?? presentation === "chip";
  const showDetails = showDetailsProp ?? defaults.showDetails ?? false;
  const counterfeit = overlay?.impersonates;
  const counterfeitText = counterfeit ? impersonationSentence(counterfeit) : void 0;
  const label = labelProp ?? addressOverlayName(overlay) ?? counterfeit?.claimed ?? fallbackLabel ?? (overlayProp === null ? void 0 : defaults.resolveFallbackLabel?.(address, chain));
  const entityLabel = entityLabelProp ?? overlay?.primaryEntityName ?? overlay?.label;
  const overlayRows = React3.useMemo(() => overlayTooltipRows(overlay), [overlay]);
  const tooltipRows = tooltipRowsProp && tooltipRowsProp.length ? [...tooltipRowsProp, ...overlayRows] : overlayRows;
  const tooltipHeaderRight = tooltipHeaderRightProp ?? (overlay?.riskLevel ? /* @__PURE__ */ jsx(RiskBadge, { level: overlay.riskLevel }) : void 0);
  const normalized = normalizeAddress(address, chain);
  const displayed = useAddressDisplayValue(address, chain);
  const handleCopy = React3.useCallback(
    (e) => {
      e.preventDefault();
      e.clipboardData.setData("text/plain", displayed);
    },
    [displayed]
  );
  const link = navigationEnabled ? href ?? (chain ? getExplorerUrl(normalized, chain, "address") : null) ?? void 0 : void 0;
  const text = /* @__PURE__ */ jsx(MiddleEllipsis, { value: displayed, kind: "address", monospace, title: displayed, className: "min-w-0 text-[length:inherit]" });
  const handleInternalClick = React3.useCallback(
    (e) => {
      e.stopPropagation();
      if (!opensInApp(e)) return;
      if (!onDrilldown) return;
      e.preventDefault();
      onDrilldown(focusAddress(chain ?? "", address, entityLabel ?? label));
    },
    [onDrilldown, chain, address, entityLabel, label]
  );
  let value;
  if (!interactive) {
    value = text;
  } else if (internalHref) {
    value = /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 max-w-full items-center gap-1", children: [
      /* @__PURE__ */ jsxs(
        "a",
        {
          href: internalHref,
          target: internalNewTab ? "_blank" : void 0,
          rel: internalNewTab ? "noreferrer noopener" : void 0,
          onClick: internalNewTab ? (e) => e.stopPropagation() : handleInternalClick,
          className: "inline-flex min-w-0 max-w-full items-center gap-1 hover:underline",
          children: [
            text,
            showInternalIcon && /* @__PURE__ */ jsx("span", { title: "Open address details", className: QUICK_ACTION_CLASS, children: /* @__PURE__ */ jsx(ArrowUpRight, { className: "size-3 shrink-0", "aria-hidden": true }) })
          ]
        }
      ),
      showExplorerIcon && link && /* @__PURE__ */ jsx(TooltipHint, { label: "Open address in block explorer", children: /* @__PURE__ */ jsx(
        "a",
        {
          href: link,
          target: "_blank",
          rel: "noreferrer noopener",
          className: cn("shrink-0 text-muted-foreground hover:text-foreground", QUICK_ACTION_CLASS),
          "aria-label": "Open in block explorer",
          onClick: (e) => e.stopPropagation(),
          children: /* @__PURE__ */ jsx(ExternalLink, { className: "size-3", "aria-hidden": true })
        }
      ) })
    ] });
  } else if (onDrilldown) {
    value = /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: (e) => {
          e.stopPropagation();
          onDrilldown(focusAddress(chain ?? "", address, entityLabel ?? label));
        },
        className: "inline-flex min-w-0 max-w-full items-center gap-1 hover:underline",
        children: text
      }
    );
  } else if (link) {
    value = /* @__PURE__ */ jsxs("a", { href: link, onClick: (e) => e.stopPropagation(), className: "inline-flex min-w-0 max-w-full items-center gap-1 hover:underline", target: "_blank", rel: "noreferrer noopener", children: [
      text,
      showExplorerIcon && /* @__PURE__ */ jsx("span", { title: "Open address in block explorer", className: QUICK_ACTION_CLASS, children: /* @__PURE__ */ jsx(ExternalLink, { className: "size-3", "aria-hidden": true }) })
    ] });
  } else {
    value = text;
  }
  const enableTooltip = richTooltip ?? Boolean(
    entityLabel || onDrilldown || internalHref || tooltipRows && tooltipRows.length || tooltipHeaderRight
  );
  if (interactive && enableTooltip) {
    const actions2 = [];
    if (onDrilldown) {
      actions2.push({
        icon: Search,
        label: "Investigate",
        onClick: () => onDrilldown(focusAddress(chain ?? "", address, entityLabel ?? label))
      });
    }
    if (link) {
      actions2.push({ icon: ExternalLink, label: "Explorer", onClick: () => window.open(link, "_blank", "noopener,noreferrer") });
    }
    value = /* @__PURE__ */ jsx(
      RichTooltip,
      {
        title: entityLabel ?? label ?? "Address",
        subtitle: /* @__PURE__ */ jsx("span", { className: "font-mono", children: displayed }),
        headerRight: tooltipHeaderRight,
        rows: tooltipRows,
        actions: actions2,
        children: value
      }
    );
  }
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    label && (() => {
      const name = /* @__PURE__ */ jsx("span", { "data-address-label": true, className: cn("min-w-0 truncate font-medium leading-tight", presentation === "inline" && "max-w-48 flex-1", counterfeit && "text-destructive"), title: counterfeitText ?? label, children: interactive && internalHref ? /* @__PURE__ */ jsx(
        "a",
        {
          href: internalHref,
          target: internalNewTab ? "_blank" : void 0,
          rel: internalNewTab ? "noreferrer noopener" : void 0,
          onClick: internalNewTab ? (e) => e.stopPropagation() : handleInternalClick,
          className: "hover:underline",
          children: label
        }
      ) : label });
      if (!counterfeit) return name;
      return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex min-w-0 max-w-full items-center gap-1", presentation === "inline" && "max-w-56"), children: [
        name,
        /* @__PURE__ */ jsx(CounterfeitMark, { claim: counterfeit })
      ] });
    })(),
    /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 max-w-full flex-wrap items-center gap-x-1.5 gap-y-1 text-muted-foreground", onCopy: handleCopy, children: [
      /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 max-w-full items-center gap-1.5", children: [
        value,
        interactive && copyable && /* @__PURE__ */ jsx(CopyButton, { value: displayed, className: cn("shrink-0", QUICK_ACTION_CLASS) }),
        interactive && onEditCatalog && /* @__PURE__ */ jsx(TooltipHint, { label: "Edit address label and catalog details", children: /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": `Edit catalog for ${normalized}`,
            "aria-haspopup": "dialog",
            className: cn("inline-flex min-h-6 min-w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground focus-ring", QUICK_ACTION_CLASS),
            onClick: (e) => {
              e.stopPropagation();
              e.currentTarget.focus();
              onEditCatalog(focusAddress(chain ?? "", address, entityLabel ?? label));
            },
            children: /* @__PURE__ */ jsx(Tag, { className: "size-3", "aria-hidden": true })
          }
        ) })
      ] }),
      showDetails && chain && /* @__PURE__ */ jsx(
        AddressFactsBadge,
        {
          address,
          chain,
          overlay,
          proxy,
          abi,
          interactive,
          getContractHref: navigationEnabled ? defaults.getContractHref : void 0,
          onNavigate: navigationEnabled ? defaults.onNavigateContract : void 0,
          navigationEnabled
        }
      )
    ] })
  ] });
  return (
    // Bounded by its container at every level. Each span here was content-sized,
    // so in a narrow column the whole block overflowed the cell and was clipped by
    // it — the ellipsis inside never engaged, a long name was cut mid-word and the
    // copy button fell outside the cell.
    /* @__PURE__ */ jsxs("span", { "data-address-display": presentation, "data-address-size": size, "data-quick-actions": actions, className: cn(
      "inline-flex min-w-0 max-w-full flex-wrap items-center gap-1.5 align-middle",
      size === "sm" ? "text-xs" : size === "md" ? "text-sm" : "text-[length:inherit]",
      presentation === "chip" && "rounded-md border bg-muted/30",
      presentation === "chip" && (size === "sm" ? "px-1.5 py-0.5" : "px-2 py-1"),
      className
    ), children: [
      showChain && chain && /* @__PURE__ */ jsx(ChainAvatar, { chain, size: "sm", showName: "sr-only" }),
      showAvatar && /* @__PURE__ */ jsx(AddressAvatar, { logoUrl: overlay?.logo, color: overlay?.color, name: label ?? normalized, size: "sm" }),
      /* @__PURE__ */ jsx("span", { className: cn("inline-flex min-w-0 max-w-full", presentation === "inline" ? "flex-wrap items-center gap-1.5" : "flex-col gap-0.5"), children: content }),
      showRiskBadge && overlay?.riskLevel && /* @__PURE__ */ jsx(RiskBadge, { level: overlay.riskLevel, className: "shrink-0" })
    ] })
  );
}
function ChipRemoveButton({
  label,
  onRemove,
  disabled,
  className
}) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        onRemove();
      },
      disabled,
      "aria-label": `Remove ${label}`,
      className: cn(
        "ml-0.5 opacity-70 transition-opacity hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-40",
        className
      ),
      children: "\xD7"
    }
  );
}
function SeverityWord({ severity }) {
  if (severity === Severity.INFO) return null;
  return /* @__PURE__ */ jsx("span", { className: "sr-only", children: ` (${severityLabel(severity)} severity)` });
}
var SEVERITY_STYLES = {
  [Severity.INFO]: "bg-muted text-muted-foreground border-muted",
  [Severity.LOW]: "bg-risk-low/15 text-risk-low border-risk-low/30",
  [Severity.MEDIUM]: "bg-risk-medium/15 text-risk-medium border-risk-medium/30",
  [Severity.HIGH]: "bg-risk-high/15 text-risk-high border-risk-high/30",
  [Severity.CRITICAL]: "bg-risk-critical/15 text-risk-critical border-risk-critical/30"
};
function TagGlyph({
  logo,
  logoCrossOrigin,
  color
}) {
  if (logo) {
    return /* @__PURE__ */ jsx(
      "img",
      {
        src: logo,
        ...logoCrossOrigin ? { crossOrigin: logoCrossOrigin } : {},
        alt: "",
        "aria-hidden": true,
        className: "-ml-0.5 size-3.5 shrink-0 rounded-full object-cover"
      }
    );
  }
  if (color) {
    return /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": true,
        className: "-ml-0.5 size-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10",
        style: { backgroundColor: color }
      }
    );
  }
  return null;
}
function Tag2({
  label,
  severity = Severity.INFO,
  logo,
  logoCrossOrigin,
  color,
  onRemove,
  removeDisabled,
  className
}) {
  return /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: cn("border gap-1", SEVERITY_STYLES[severity], className), children: [
    /* @__PURE__ */ jsx(TagGlyph, { logo, logoCrossOrigin, color }),
    /* @__PURE__ */ jsx("span", { children: label }),
    /* @__PURE__ */ jsx(SeverityWord, { severity }),
    onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
  ] });
}
var SCOPE_STYLES = {
  user: { ring: "border-brand/40", dot: "bg-brand", title: "Your override" },
  tenant: { ring: "border-finality-confirmed/40", dot: "bg-finality-confirmed", title: "Tenant override" },
  // Also previously unreachable: an org-wide overlay reported itself as one
  // tenant's, so a reader in a sibling tenant could not tell why they saw it.
  org: { ring: "border-finality-pending/40", dot: "bg-finality-pending", title: "Set for your organization" },
  provider: { ring: "border-risk-medium/40", dot: "bg-risk-medium", title: "Provider candidate" },
  entity: { ring: "border-finality-finalized/40", dot: "bg-finality-finalized", title: "Inherited from entity" },
  // Named for what it IS, because readers now actually see it. This tier was
  // unreachable — the backend classified every platform-shipped row as a
  // `tenant` override — so "Default" was a word nothing ever rendered. On an
  // account whose catalog is only the bundled set, it is now the commonest chip
  // on the screen, and "Default" alone reads as "unset" rather than "built in".
  default: { ring: "border-muted", dot: "bg-muted-foreground/50", title: "Platform default \u2014 built in, not editable" }
};
function CatalogLabel({
  label,
  value,
  scope,
  showScope,
  severity,
  logo,
  logoCrossOrigin,
  color,
  onRemove,
  removeDisabled,
  className
}) {
  const style = scope ? SCOPE_STYLES[scope] : null;
  const sharedLadder = useLadderShared();
  const withScope = sharedLadder && (showScope ?? Boolean(scope));
  const weight = severity && severity !== Severity.INFO ? SEVERITY_STYLES[severity] : void 0;
  return /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: cn("gap-1.5 border", weight ?? style?.ring, className), children: [
    logo ? /* @__PURE__ */ jsx(
      "img",
      {
        src: logo,
        ...logoCrossOrigin ? { crossOrigin: logoCrossOrigin } : {},
        alt: "",
        "aria-hidden": true,
        className: "-ml-0.5 size-3.5 shrink-0 rounded-full object-cover"
      }
    ) : color ? /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": true,
        className: "-ml-0.5 size-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10",
        style: { backgroundColor: color }
      }
    ) : null,
    withScope && style && // The dot's whole meaning was a `title` on an `aria-hidden` element —
    // a tooltip nothing could reach and a name nothing could read, so
    // provenance ("Your override", "Platform default") existed for the
    // mouse alone. `role="img"` + `aria-label` is the pattern
    // `CriticalityBadge` already uses for exactly this: a glyph that is
    // decoration to look at and a sentence to listen to. The `title` stays
    // for the pointer, now on an element that is actually exposed.
    /* @__PURE__ */ jsx(
      "span",
      {
        role: "img",
        "aria-label": style.title,
        title: style.title,
        className: cn("size-1.5 rounded-full", style.dot)
      }
    ),
    /* @__PURE__ */ jsx("span", { className: "font-medium", children: label }),
    value != null && /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: value }),
    severity && /* @__PURE__ */ jsx(SeverityWord, { severity }),
    onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
  ] });
}
var ACCENTS = [
  "text-category-blue bg-category-blue/10 border-category-blue/30",
  "text-category-violet bg-category-violet/10 border-category-violet/30",
  "text-category-emerald bg-category-emerald/10 border-category-emerald/30",
  "text-category-amber bg-category-amber/10 border-category-amber/30",
  "text-category-rose bg-category-rose/10 border-category-rose/30",
  "text-category-cyan bg-category-cyan/10 border-category-cyan/30",
  "text-category-pink bg-category-pink/10 border-category-pink/30"
];
function accentFor(id) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = hash * 31 + id.charCodeAt(i) >>> 0;
  return ACCENTS[hash % ACCENTS.length];
}
function GroupBadge({
  groupId,
  name,
  count,
  icon,
  onClick,
  onRemove,
  removeDisabled,
  className
}) {
  const accent = accentFor(groupId);
  const label = name ?? groupId;
  const body = /* @__PURE__ */ jsxs(Fragment, { children: [
    icon ?? /* @__PURE__ */ jsx(Boxes, { className: "size-3" }),
    /* @__PURE__ */ jsx("span", { children: label }),
    count != null && /* @__PURE__ */ jsxs("span", { className: "opacity-70", children: [
      "\xB7 ",
      count
    ] })
  ] });
  const shell = cn(
    "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
    accent,
    className
  );
  if (onClick) {
    return /* @__PURE__ */ jsxs("span", { className: cn(shell, "pr-1"), title: label, children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick,
          className: "inline-flex items-center gap-1.5 transition-opacity hover:opacity-80",
          children: body
        }
      ),
      onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
    ] });
  }
  return /* @__PURE__ */ jsxs("span", { className: cn(shell, onRemove && "pr-1"), title: label, children: [
    body,
    onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
  ] });
}
function splitOverflow(total, max) {
  const safeMax = Math.max(0, max);
  if (total <= safeMax) return { visible: total, overflow: 0 };
  const visible = Math.max(0, safeMax - 1);
  return { visible, overflow: total - visible };
}
function TagList({
  items,
  max = 3,
  popoverTitle,
  className,
  align = "start"
}) {
  if (items.length === 0) return null;
  const { visible, overflow } = splitOverflow(items.length, max);
  return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex flex-wrap items-center gap-1", className), children: [
    items.slice(0, visible).map((item, i) => /* @__PURE__ */ jsx(React3.Fragment, { children: item }, i)),
    overflow > 0 && /* @__PURE__ */ jsxs(Popover, { children: [
      /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: (e) => e.stopPropagation(),
          className: "inline-flex",
          "aria-label": `Show ${overflow} more`,
          children: /* @__PURE__ */ jsxs(
            Badge,
            {
              variant: "outline",
              className: "cursor-pointer text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              children: [
                "+",
                overflow
              ]
            }
          )
        }
      ) }),
      /* @__PURE__ */ jsxs(PopoverContent, { align, className: "w-80 p-2", children: [
        popoverTitle && /* @__PURE__ */ jsx("div", { className: "mb-2 px-1 text-xs font-medium text-muted-foreground", children: popoverTitle }),
        /* @__PURE__ */ jsx("div", { className: "flex max-h-64 flex-wrap gap-1 overflow-y-auto", children: items.map((item, i) => /* @__PURE__ */ jsx(React3.Fragment, { children: item }, i)) })
      ] })
    ] })
  ] });
}
var Context = React3.createContext({ policy: readConfidencePolicy(), loading: false });
function ReadConfidenceProvider({ policy, loading = false, children }) {
  const value = React3.useMemo(() => ({ policy, loading }), [policy, loading]);
  return /* @__PURE__ */ jsx(Context.Provider, { value, children });
}
function useReadConfidence() {
  const { policy, loading } = React3.useContext(Context);
  return { policy, loading, accepts: (score) => !loading && meetsReadConfidence(policy, score) };
}
function ReadConfidenceBadge({ score, evidence, explanation, lifecycle, record, confidenceNote, diagnostics }) {
  const cutoffs = useTierCutoffs();
  const valid = typeof score === "number" && Number.isFinite(score) && score >= 0 && score <= 1;
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 text-xs", children: [
    /* @__PURE__ */ jsx(Badge, { variant: "outline", children: valid ? `Confidence ${Number((score * 100).toFixed(2))}%` : "Not scored" }),
    /* @__PURE__ */ jsx(
      WhyPopover,
      {
        confidence: valid ? score : null,
        confidenceNote: valid ? null : confidenceNote ?? "No confidence was recorded for this interpretation.",
        evidence,
        explanation,
        lifecycle,
        record,
        diagnostics,
        cutoffs
      }
    )
  ] });
}
function readConfidenceHiddenText({
  subject,
  score,
  policy,
  loading
}) {
  if (loading) return "Loading read confidence preference\u2026";
  const reason = score == null ? "unscored findings are excluded" : `floor ${Number((policy.floor * 100).toFixed(2))}%`;
  return `${subject} hidden by your read confidence preference (${reason}). Change it in Settings \u2192 Defaults to review this interpretation.`;
}
function ReadConfidenceBoundary({ children, rawFallback, subject = "Interpretation", ...assessment }) {
  const { accepts, policy, loading } = useReadConfidence();
  const [revealed, setRevealed] = React3.useState(false);
  const passes = accepts(assessment.score);
  const visible = passes || revealed;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", "data-confidence-visible": visible, "data-confidence-passes": passes, children: [
    /* @__PURE__ */ jsx(ReadConfidenceBadge, { ...assessment }),
    passes ? children : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", role: "status", children: readConfidenceHiddenText({ subject, score: assessment.score, policy, loading }) }),
      loading ? null : /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => setRevealed(!revealed),
          className: "text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground",
          children: revealed ? "Hide it again" : "Show it anyway"
        }
      ),
      revealed ? children : null,
      rawFallback
    ] })
  ] });
}
function primaryFirst(items) {
  return [...items].sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)));
}
var SEVERITY_RANK = { low: 1, medium: 2, high: 3, critical: 4 };
function chipText(item) {
  const text = item.value ? `${item.name}: ${item.value}` : item.name;
  return String(text ?? "").trim().toLowerCase();
}
function distinctChips(items, shownAs) {
  const shown = String(shownAs ?? "").trim().toLowerCase();
  const best = /* @__PURE__ */ new Map();
  for (const item of primaryFirst(items)) {
    const text = chipText(item);
    if (!text || shown && text === shown) continue;
    const kept = best.get(text);
    const rank = SEVERITY_RANK[String(item.severity ?? "").toLowerCase()] ?? 0;
    const keptRank = kept ? SEVERITY_RANK[String(kept.severity ?? "").toLowerCase()] ?? 0 : -1;
    if (!kept || rank > keptRank) best.set(text, item);
  }
  return [...best.values()];
}
function ChipSlot({
  kind,
  item,
  prefs,
  renderChip,
  renderEntityLink
}) {
  const img = useSignedCdnImage(prefs.logos ? item.logo : void 0);
  const sharedLadder = useLadderShared();
  const visiblePrefs = sharedLadder ? prefs : { ...prefs, showProvenance: false };
  const glyph = prefs.logos ? { logo: img.src, logoCrossOrigin: img.crossOrigin, color: item.color } : {};
  const chip = chipFor(kind, item, visiblePrefs, glyph, renderEntityLink);
  const rendered = renderChip ? renderChip(chip, item, kind) : chip;
  if (!visiblePrefs.showWhy || rendered == null) return /* @__PURE__ */ jsx(Fragment, { children: rendered });
  return /* @__PURE__ */ jsx(
    WhyPopover,
    {
      confidence: scoredConfidence(item.confidence, item),
      explanation: item.explanation,
      lifecycle: item.lifecycle,
      title: item.name,
      children: rendered
    }
  );
}
function chipFor(kind, item, prefs, glyph, renderEntityLink) {
  switch (kind) {
    case "label":
      return /* @__PURE__ */ jsx(
        CatalogLabel,
        {
          label: item.name,
          value: item.value,
          scope: item.scope,
          showScope: prefs.showProvenance && Boolean(item.scope),
          severity: item.severity,
          onRemove: item.onRemove,
          removeDisabled: item.removeDisabled,
          ...glyph
        }
      );
    case "tag":
      return /* @__PURE__ */ jsx(
        Tag2,
        {
          label: item.name,
          severity: item.severity,
          onRemove: item.onRemove,
          removeDisabled: item.removeDisabled,
          ...glyph
        }
      );
    case "group":
      return /* @__PURE__ */ jsx(
        GroupBadge,
        {
          groupId: item.id,
          name: item.name,
          count: item.count,
          onRemove: item.onRemove,
          removeDisabled: item.removeDisabled
        }
      );
    case "entity":
      return /* @__PURE__ */ jsxs(Badge, { variant: "secondary", className: "gap-1.5 font-normal", title: item.entityType ?? item.name, children: [
        item.isPrimary && // The headline entity for this address, not merely one it carries.
        // Sorting it first is not enough on a row of three — the star is
        // what says which one the reconciler will actually use.
        /* @__PURE__ */ jsx(Star, { "aria-label": "Primary", className: "-ml-0.5 size-3 shrink-0 fill-current text-highlight" }),
        glyph.logo ? /* @__PURE__ */ jsx(
          "img",
          {
            src: glyph.logo,
            ...glyph.logoCrossOrigin ? { crossOrigin: glyph.logoCrossOrigin } : {},
            alt: "",
            "aria-hidden": true,
            className: "-ml-0.5 size-3.5 shrink-0 rounded-full object-cover"
          }
        ) : glyph.color ? /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": true,
            className: "-ml-0.5 size-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10",
            style: { backgroundColor: glyph.color }
          }
        ) : null,
        /* @__PURE__ */ jsx("span", { children: renderEntityLink ? renderEntityLink(item, item.name) : item.name }),
        item.onRemove && /* @__PURE__ */ jsx(
          ChipRemoveButton,
          {
            label: item.name,
            onRemove: item.onRemove,
            disabled: item.removeDisabled
          }
        )
      ] });
  }
}
function AttributionChips({
  bundle,
  withheld = 0,
  floorMode = "hide",
  userPrefs,
  popoverTitle = "Attribution",
  renderChip,
  renderEntityLink,
  empty = null,
  className,
  shownAs,
  ...override
}) {
  const prefs = resolveAttributionChipPrefs(override, userPrefs);
  const { policy, loading } = useReadConfidence();
  const withheldByServer = Math.max(0, Math.trunc(withheld) || 0);
  const { items, hidden } = React3.useMemo(() => {
    const out = [];
    let hidden2 = 0;
    for (const kind of prefs.kinds) {
      for (const item of distinctChips(bundle[BUNDLE_KEY[kind]] ?? [], shownAs)) {
        const score = scoredConfidence(item.confidence, item);
        const below = !loading && policy.floor > 0 && typeof score === "number" && score < policy.floor;
        if (below && floorMode === "hide") {
          hidden2 += 1;
          continue;
        }
        const slot = /* @__PURE__ */ jsx(
          ChipSlot,
          {
            kind,
            item,
            prefs,
            renderChip,
            renderEntityLink
          },
          `${kind}:${item.id}`
        );
        out.push(
          below ? /* @__PURE__ */ jsx(
            "span",
            {
              className: "inline-flex opacity-60",
              "data-below-floor": "true",
              title: `Below your ${Number((policy.floor * 100).toFixed(2))}% confidence floor \u2014 this is not shown on reading surfaces.`,
              children: slot
            },
            `${kind}:${item.id}`
          ) : slot
        );
      }
    }
    return { items: out, hidden: hidden2 + withheldByServer };
  }, [bundle, floorMode, loading, policy.floor, prefs, renderChip, renderEntityLink, shownAs, withheldByServer]);
  const hiddenNote = hidden > 0 ? /* @__PURE__ */ jsxs(
    Badge,
    {
      variant: "outline",
      className: "border-dashed font-normal text-muted-foreground",
      title: `${hidden} attribution${hidden === 1 ? "" : "s"} scored below the minimum confidence your policy accepts (${Number(
        (policy.floor * 100).toFixed(2)
      )}%). Change it in Settings \u2192 Defaults.`,
      children: [
        hidden,
        " below your floor"
      ]
    },
    "__below_floor__"
  ) : null;
  if (items.length === 0) return hiddenNote ? /* @__PURE__ */ jsx("span", { className: "inline-flex", children: hiddenNote }) : /* @__PURE__ */ jsx(Fragment, { children: empty });
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex flex-wrap items-center gap-1", children: [
    /* @__PURE__ */ jsx(
      TagList,
      {
        items,
        max: prefs.max,
        popoverTitle,
        className: cn(prefs.size === "sm" && "text-xs", className)
      }
    ),
    hiddenNote
  ] });
}

export { AddressAvatar, AddressDisplay, AddressDisplayProvider, AttributionChips, CatalogLabel, ChipRemoveButton, ConfidenceCutoffsProvider, CounterfeitMark, GroupBadge, KIND_PHRASE, LifecycleBadge, RISK_LEVEL_LABELS, RISK_LEVEL_STYLES, ReadConfidenceBadge, ReadConfidenceBoundary, ReadConfidenceProvider, RiskBadge, SEVERITY_STYLES, SeverityWord, Tag2 as Tag, TagList, TransactionFlagBadge, TransactionFlagNotice, WhyPanel, WhyPopover, addressOverlayName, explainEvidence, lifecycleDescription, readConfidenceHiddenText, splitOverflow, transactionFlagChipClass, useAddressDisplayName, useAddressDisplayValue, useCutoffsAreScoped, useReadConfidence, useTierCutoffs, useTierFor };
//# sourceMappingURL=chunk-4FACZLH7.js.map
//# sourceMappingURL=chunk-4FACZLH7.js.map